import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import AvaliacaoRH from '../components/AvaliacaoRH';

// Avaliação dos serviços pelos participantes — resultados, comentários por aprovar, empresas por associar,
// pedidos de contacto e links do questionário (hub.workwell.pt/avaliar).
const C = { roxo:'#691FB1', roxoClaro:'#EDE7F6', rosa:'#FF357B', verde:'#27AE60', verdeClaro:'#EAFAF1',
  ambar:'#B9770E', ambarClaro:'#FEF5E7', verm:'#C0392B', vermClaro:'#FDECEA',
  txt:'#1A1A2E', med:'#4A4A6A', claro:'#8888AA', borda:'#E4DEFA', fundo:'#F7F5FC', branco:'#FFFFFF' };
const F = "'DM Sans',sans-serif";
const inp = { padding:'7px 10px', border:`1px solid ${C.borda}`, borderRadius:8, fontSize:12.5, fontFamily:F, color:C.txt, background:C.branco };
const btn = (cor=C.roxo, cheio=true) => ({ padding:'6px 12px', borderRadius:8, border: cheio ? 'none' : `1px solid ${cor}`, background: cheio ? cor : 'transparent', color: cheio ? '#fff' : cor, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:F });
const fmtD = (d) => d ? `${String(d).slice(8,10)}/${String(d).slice(5,7)}/${String(d).slice(2,4)}` : '';
const r1 = (v) => v == null || isNaN(v) ? '—' : (Math.round(v*10)/10).toLocaleString('pt-PT');
const mesesAtras = (n) => { const d = new Date(); d.setMonth(d.getMonth()-n); return d.toISOString().slice(0,10); };

// indicadores de um conjunto de respostas
export function indicadores(rs) {
  const n = rs.length;
  const media = (xs) => xs.length ? xs.reduce((a,b)=>a+b,0)/xs.length : null;
  const sat = media(rs.map(r => Number(r.satisfacao)).filter(v => !isNaN(v) && v > 0));
  const npsV = rs.map(r => r.nps).filter(v => v != null);
  const nps = npsV.length ? Math.round(((npsV.filter(v => v >= 9).length - npsV.filter(v => v <= 6).length) / npsV.length) * 100) : null;
  const pares = rs.filter(r => r.antes != null && r.depois != null);
  const delta = media(pares.map(r => r.depois - r.antes));
  const apl = rs.map(r => r.aplicar).filter(v => v != null);
  const aplicar = apl.length ? Math.round(apl.filter(v => v >= 4).length / apl.length * 100) : null;
  return { n, sat, nps, delta, aplicar };
}

export default function AvaliacoesPage({ userEmail, userRole }) {
  const [rs, setRs] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [cfg, setCfg] = useState([]);
  const [users, setUsers] = useState([]);
  const [de, setDe] = useState(mesesAtras(3));
  const [ate, setAte] = useState(new Date().toISOString().slice(0,10));
  const [fCli, setFCli] = useState('');
  const [fServ, setFServ] = useState('');
  const [tab, setTab] = useState('resultados');
  const [linkCli, setLinkCli] = useState('');
  const [copiado, setCopiado] = useState(false);
  const podeContactos = ['admin','tl'].includes(userRole);

  const carregar = useCallback(async () => {
    let todas = [];
    for (let i = 0; ; i++) {
      const { data, error } = await supabase.from('avaliacao_servico_respostas').select('*').order('created_at', { ascending:false }).range(i*1000, i*1000+999);
      if (error || !data) break; todas = todas.concat(data); if (data.length < 1000) break;
    }
    setRs(todas);
    const [{ data: cl }, { data: cf }, { data: us }] = await Promise.all([
      supabase.from('clientes').select('id,nome').order('nome'),
      supabase.from('avaliacao_servico_cfg').select('*').order('area'),
      supabase.from('user_roles').select('email,full_name,active').order('full_name'),
    ]);
    setClientes(cl || []); setCfg(cf || []); setUsers((us || []).filter(u => u.active !== false));
    if (podeContactos) { const { data: ct } = await supabase.from('avaliacao_servico_contactos').select('*').order('created_at', { ascending:false }).limit(300); setContactos(ct || []); }
  }, [podeContactos]);
  useEffect(() => { carregar(); }, [carregar]);

  const nomeCli = useMemo(() => Object.fromEntries(clientes.map(c => [String(c.id), c.nome])), [clientes]);
  const filtradas = rs.filter(r => {
    const d = String(r.sessao_data || r.created_at).slice(0,10);
    return d >= de && d <= ate && (!fCli || String(r.cliente_id) === fCli) && (!fServ || r.servico === fServ) && r.match_estado !== 'ignorar';
  });
  const servicos = [...new Set(rs.map(r => r.servico))].map(v => ({ v, l: rs.find(r => r.servico === v)?.servico_label || v }));
  const tot = indicadores(filtradas);
  const agrupar = (chave) => {
    const g = {}; filtradas.forEach(r => { const k = chave(r) || '—'; (g[k] = g[k] || []).push(r); });
    return Object.entries(g).map(([k, xs]) => ({ k, ...indicadores(xs) })).sort((a,b) => b.n - a.n);
  };
  const porAprovar = rs.filter(r => r.comentarios_estado === 'pendente');
  const porAssociar = rs.filter(r => r.match_estado === 'por_associar');
  const contactosAbertos = contactos.filter(c => !c.tratado_em);

  const atualizar = async (id, campos) => { await supabase.from('avaliacao_servico_respostas').update(campos).eq('id', id); setRs(prev => prev.map(r => r.id === id ? { ...r, ...campos } : r)); };
  const associar = async (r, cid) => { if (!cid) return; await atualizar(r.id, { cliente_id: cid, match_estado:'manual' }); };
  const tratarContacto = async (c) => { await supabase.from('avaliacao_servico_contactos').update({ tratado_em:new Date().toISOString(), tratado_por:userEmail }).eq('id', c.id); carregar(); };
  const gravarCfg = async (area, email) => { await supabase.from('avaliacao_servico_cfg').update({ responsavel_email: email || null }).eq('area', area); carregar(); };

  const Kpi = ({ t, v, sub }) => (
    <div style={{ background:C.branco, border:`1px solid ${C.borda}`, borderRadius:12, padding:'12px 16px', minWidth:130, flex:1 }}>
      <div style={{ fontSize:11.5, color:C.claro, fontWeight:700, textTransform:'uppercase', letterSpacing:0.6 }}>{t}</div>
      <div style={{ fontSize:24, fontWeight:800, color:C.roxo, marginTop:2 }}>{v}</div>
      {sub && <div style={{ fontSize:11.5, color:C.claro }}>{sub}</div>}
    </div>);
  const Tabela = ({ titulo, linhas }) => (
    <div style={{ background:C.branco, border:`1px solid ${C.borda}`, borderRadius:12, padding:'12px 16px', marginBottom:14 }}>
      <div style={{ fontSize:13, fontWeight:800, color:C.roxo, marginBottom:8 }}>{titulo}</div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
        <thead><tr style={{ color:C.claro, textAlign:'left' }}><th style={{ padding:'4px 6px' }}></th><th>Respostas</th><th>Satisfação</th><th>NPS</th><th>Antes → depois</th><th>Aplica no dia a dia</th></tr></thead>
        <tbody>{linhas.map(l => (
          <tr key={l.k} style={{ borderTop:`1px solid ${C.borda}` }}>
            <td style={{ padding:'5px 6px', fontWeight:600, color:C.txt }}>{l.k}</td><td>{l.n}</td>
            <td style={{ color: l.sat != null && l.sat < 3 ? C.verm : C.txt }}>{r1(l.sat)}</td><td>{l.nps ?? '—'}</td>
            <td>{l.delta == null ? '—' : (l.delta > 0 ? '+' : '') + r1(l.delta)}</td><td>{l.aplicar == null ? '—' : l.aplicar + '%'}</td>
          </tr>))}</tbody>
      </table>
    </div>);
  const Tab = ({ id, t, n }) => (
    <button onClick={()=>setTab(id)} style={{ padding:'7px 12px', borderRadius:8, border:`1px solid ${tab===id?C.roxo:C.borda}`, background:tab===id?C.roxoClaro:C.branco, color:tab===id?C.roxo:C.med, fontSize:12.5, fontWeight:700, cursor:'pointer', fontFamily:F }}>
      {t}{n ? <span style={{ marginLeft:6, background:C.rosa, color:'#fff', borderRadius:9, padding:'0 6px', fontSize:11 }}>{n}</span> : null}</button>);

  const link = linkCli ? `${window.location.origin}/avaliar#${encodeURIComponent(nomeCli[linkCli] || '').replace(/%20/g, '_')}` : `${window.location.origin}/avaliar`;

  return (
    <div style={{ padding:'24px 28px', fontFamily:F }}>
      <h1 style={{ fontSize:21, color:C.roxo, margin:0, fontFamily:"'Fraunces',serif" }}>Avaliação dos serviços</h1>
      <p style={{ fontSize:12.5, color:C.claro, margin:'3px 0 14px' }}>Respostas anónimas dos participantes ao questionário {window.location.host}/avaliar</p>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
        <Tab id="resultados" t="Resultados"/>
        <Tab id="comentarios" t="Comentários por aprovar" n={porAprovar.length}/>
        <Tab id="associar" t="Empresas por associar" n={porAssociar.length}/>
        {podeContactos && <Tab id="contactos" t="Pedidos de contacto" n={contactosAbertos.length}/>}
        <Tab id="links" t="Links do questionário"/>
        {userRole === 'admin' && <Tab id="rh" t="Avaliação dos RH"/>}
        {userRole === 'admin' && <Tab id="cfg" t="Definições"/>}
      </div>

      {tab === 'resultados' && (<>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
          <input type="date" style={inp} value={de} onChange={e=>setDe(e.target.value)}/>
          <span style={{ color:C.claro, fontSize:12 }}>até</span>
          <input type="date" style={inp} value={ate} onChange={e=>setAte(e.target.value)}/>
          <select style={inp} value={fCli} onChange={e=>setFCli(e.target.value)}><option value="">Todos os clientes</option>{[...new Set(rs.map(r => r.cliente_id).filter(Boolean).map(String))].sort((a,b)=>(nomeCli[a]||'').localeCompare(nomeCli[b]||'')).map(id => <option key={id} value={id}>{nomeCli[id] || id}</option>)}</select>
          <select style={inp} value={fServ} onChange={e=>setFServ(e.target.value)}><option value="">Todos os serviços</option>{servicos.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}</select>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
          <Kpi t="Respostas" v={tot.n}/>
          <Kpi t="Satisfação" v={r1(tot.sat)} sub="média de 1 a 5"/>
          <Kpi t="NPS" v={tot.nps ?? '—'} sub="% promotores − % detratores"/>
          <Kpi t="Antes → depois" v={tot.delta == null ? '—' : (tot.delta > 0 ? '+' : '') + r1(tot.delta)} sub="variação média (0 a 5)"/>
          <Kpi t="Aplica no dia a dia" v={tot.aplicar == null ? '—' : tot.aplicar + '%'} sub="respostas 4 ou 5"/>
        </div>
        <Tabela titulo="Por serviço" linhas={agrupar(r => r.servico_label)}/>
        <Tabela titulo="Por profissional (interno — não aparece no portal)" linhas={agrupar(r => r.profissional)}/>
        <Tabela titulo="Por cliente" linhas={agrupar(r => r.cliente_id ? nomeCli[String(r.cliente_id)] : '(por associar)')}/>
        <Tabela titulo="Por mês" linhas={agrupar(r => String(r.sessao_data || r.created_at).slice(0,7)).sort((a,b) => a.k.localeCompare(b.k))}/>
      </>)}

      {tab === 'comentarios' && (<>
        <p style={{ fontSize:12.5, color:C.med }}>Só os comentários aprovados aparecem no portal do cliente. Rejeita os ofensivos ou que identifiquem alguém.</p>
        {!porAprovar.length && <div style={{ color:C.claro, fontSize:13 }}>Não há comentários por aprovar.</div>}
        {porAprovar.map(r => (
          <div key={r.id} style={{ background:C.branco, border:`1px solid ${C.borda}`, borderRadius:10, padding:'10px 14px', marginBottom:8 }}>
            <div style={{ fontSize:12, color:C.claro, marginBottom:4 }}>{fmtD(r.sessao_data)} · {nomeCli[String(r.cliente_id)] || r.empresa_texto} · {r.servico_label}{r.profissional ? ' · ' + r.profissional : ''}</div>
            {r.valorizou && <div style={{ fontSize:13, color:C.txt }}><b>Valorizou:</b> {r.valorizou}</div>}
            {r.melhorar && <div style={{ fontSize:13, color:C.txt }}><b>A melhorar:</b> {r.melhorar}</div>}
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              <button style={btn(C.verde)} onClick={()=>atualizar(r.id, { comentarios_estado:'aprovado' })}>Aprovar</button>
              <button style={btn(C.verm, false)} onClick={()=>atualizar(r.id, { comentarios_estado:'rejeitado' })}>Não mostrar no portal</button>
            </div>
          </div>))}
      </>)}

      {tab === 'associar' && (<>
        <p style={{ fontSize:12.5, color:C.med }}>Respostas em que o HUB não reconheceu a empresa escrita. Associa ao cliente certo ou ignora (ex.: respostas de teste).</p>
        {!porAssociar.length && <div style={{ color:C.claro, fontSize:13 }}>Nada por associar.</div>}
        {porAssociar.map(r => (
          <div key={r.id} style={{ display:'flex', gap:8, alignItems:'center', background:C.branco, border:`1px solid ${C.borda}`, borderRadius:10, padding:'8px 12px', marginBottom:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:12, color:C.claro, minWidth:64 }}>{fmtD(r.created_at)}</span>
            <span style={{ fontWeight:700, fontSize:13, color:C.txt, minWidth:160 }}>«{r.empresa_texto || '—'}»</span>
            <span style={{ fontSize:12, color:C.med, flex:1 }}>{r.servico_label}</span>
            <select style={inp} defaultValue="" onChange={e=>associar(r, e.target.value)}><option value="">Associar a…</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}</select>
            <button style={btn(C.claro, false)} onClick={()=>atualizar(r.id, { match_estado:'ignorar' })}>Ignorar</button>
          </div>))}
      </>)}

      {tab === 'contactos' && podeContactos && (<>
        <p style={{ fontSize:12.5, color:C.med }}>Participantes que pediram para ser contactados. O email só é visível aqui (administração e TL).</p>
        {!contactos.length && <div style={{ color:C.claro, fontSize:13 }}>Ainda não há pedidos.</div>}
        {contactos.map(c => { const r = rs.find(x => x.id === c.resposta_id) || {}; return (
          <div key={c.id} style={{ display:'flex', gap:10, alignItems:'center', background:C.branco, border:`1px solid ${C.borda}`, borderRadius:10, padding:'8px 12px', marginBottom:6, flexWrap:'wrap', opacity:c.tratado_em?0.55:1 }}>
            <span style={{ fontSize:12, color:C.claro, minWidth:64 }}>{fmtD(c.created_at)}</span>
            <span style={{ fontWeight:700, fontSize:13 }}>{c.email}</span>
            <span style={{ fontSize:12, color:C.med, flex:1 }}>{nomeCli[String(r.cliente_id)] || r.empresa_texto} · {r.servico_label} · satisfação {r1(r.satisfacao)}{r.melhorar ? ` · «${r.melhorar}»` : ''}</span>
            {c.tratado_em ? <span style={{ fontSize:12, color:C.verde }}>✓ tratado</span> : <button style={btn(C.verde)} onClick={()=>tratarContacto(c)}>Marcar como tratado</button>}
          </div>); })}
      </>)}

      {tab === 'rh' && userRole === 'admin' && <AvaliacaoRH/>}

      {tab === 'links' && (
        <div style={{ background:C.branco, border:`1px solid ${C.borda}`, borderRadius:12, padding:'14px 16px', maxWidth:720 }}>
          <p style={{ fontSize:12.5, color:C.med, marginTop:0 }}>O link de cada cliente já leva a empresa preenchida. O link geral serve para eventos com várias empresas: a pessoa escreve a empresa e o HUB reconhece-a.</p>
          <select style={{ ...inp, marginBottom:10 }} value={linkCli} onChange={e=>{ setLinkCli(e.target.value); setCopiado(false); }}><option value="">Link geral (sem empresa)</option>{clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}</select>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input readOnly style={{ ...inp, flex:1 }} value={link}/>
            <button style={btn()} onClick={()=>{ try { navigator.clipboard.writeText(link); setCopiado(true); } catch (e) {} }}>{copiado ? '✓ Copiado' : 'Copiar'}</button>
          </div>
        </div>)}

      {tab === 'cfg' && userRole === 'admin' && (
        <div style={{ background:C.branco, border:`1px solid ${C.borda}`, borderRadius:12, padding:'14px 16px', maxWidth:620 }}>
          <p style={{ fontSize:12.5, color:C.med, marginTop:0 }}>Quem recebe os pedidos de contacto (com tarefa) e os alertas de nota baixa (média abaixo de 3), por área.</p>
          {cfg.map(c => (
            <div key={c.area} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:8 }}>
              <span style={{ fontWeight:700, minWidth:90 }}>{c.area}</span>
              <select style={inp} value={c.responsavel_email || ''} onChange={e=>gravarCfg(c.area, e.target.value)}><option value="">—</option>{users.map(u => <option key={u.email} value={u.email}>{u.full_name || u.email}</option>)}</select>
            </div>))}
          <p style={{ fontSize:11.5, color:C.claro }}>Vitality: aulas, mindfulness, fisioterapia, Quickmassage, osteopatia, rastreios e eventos. Mind: formações e webinars.</p>
        </div>)}
    </div>);
}
